import React from "react"
import { Tooltip } from "antd"

interface FeatureHintProps {
  /** One or two sentences. Explains why the thing exists, not what it is called. */
  children: React.ReactNode
  /** Sits inline after a heading or a control label. */
  className?: string
}

/**
 * A small "?" that explains a feature people have not seen before.
 *
 * Most of leave V2 is new, and several parts behave in ways that are reasonable but not
 * guessable — a plan that deducts nothing, a report that counts a request in the month it
 * starts, a saved view that belongs to one report. Those need a sentence at the point of use;
 * putting them in release notes means answering the same question forever.
 *
 * Deliberately not a tour or a dismissible callout: this stays available on the tenth visit,
 * costs nothing to ignore, and does not need state to remember who has seen it.
 */
export const FeatureHint: React.FC<FeatureHintProps> = ({
  children,
  className = "",
}) => (
  <Tooltip
    title={<span className="font-avenir text-xs leading-relaxed">{children}</span>}
    overlayStyle={{ maxWidth: 320 }}
  >
    <span
      role="img"
      aria-label="What is this?"
      tabIndex={0}
      className={`inline-flex items-center justify-center w-4 h-4 rounded-full border border-neutral40 text-[10px] leading-none text-padeSubtext cursor-help align-middle hover:border-primary500 hover:text-primary500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500 ${className}`}
    >
      ?
    </span>
  </Tooltip>
)

export default FeatureHint
